import moment from 'moment';
import { Action, Dispatch } from 'redux';
import {
  API_PORTAL_PREFERENCES,
  HttpStatusCode,
  INTERCEPT_SURVEY_THRESHOLD,
  RequestId,
  API_DEFAULT_RETRIES,
  NAMESPACE_PREFERENCES,
} from 'appConstants';
import { ApiRequest } from 'services';
import {
  PreferenceError,
  PreferenceResponse,
  Preferences,
  UpdateTicketDraftContent as IndividualUpdateTicketDraft,
  UpdateTicketDraft as UpdateTicketDraftCollection,
  UpdateTicketDraft,
} from 'types';
import { NewTicketPayLoad } from 'reducers';

export interface PortalPreferenceAction extends Action {
  readonly preferenceCollection?: {
    [key: string]:
      | boolean
      | number
      | string
      | IndividualUpdateTicketDraft
      | UpdateTicketDraftCollection;
  };
  readonly preferences?: Preferences;
  readonly preferenceSpace?: string;
}

export interface CreateTicketDraft extends NewTicketPayLoad {
  readonly selectedStep: number;
  readonly timeStamp: string;
  readonly bpitTemplateId?: string;
  readonly formData?: object;
}

enum Actions {
  SET_PORTAL_APP_PREFERENCES = 'SET_PORTAL_APP_PREFERENCES',
  SET_PREFERENCE = 'SET_PREFERENCE',
  DELETE_PREFERENCES = 'DELETE_PREFERENCES',
  SKIP_PREFERENCES_CALL = 'SKIP_PREFERENCES_CALL',
}

const initialState = {
  interceptSurvey: {
    lastSurveyed: moment()
      .subtract(INTERCEPT_SURVEY_THRESHOLD, 'd')
      .toISOString(),
    optOut: false,
  },
  welcome: {
    firstTimeUser: false,
  },
  createTicketDraft: {} as CreateTicketDraft,
  updateTicketDraft: {} as UpdateTicketDraft,
  skipPreferencesCall: false,
};

// State to return if the user has no Preferences set
const firstTimeUserState = {
  interceptSurvey: {
    lastSurveyed: moment()
      .subtract(INTERCEPT_SURVEY_THRESHOLD, 'd')
      .toISOString(),
    optOut: false,
  },
  welcome: {
    firstTimeUser: true,
  },
  createTicketDraft: {} as CreateTicketDraft,
  updateTicketDraft: {} as UpdateTicketDraft,
  skipPreferencesCall: false,
};

// State to return if we fail to return preferences
const failureState = {
  interceptSurvey: {
    lastSurveyed: moment().toISOString(),
    optOut: true,
  },
  welcome: {
    firstTimeUser: false,
  },
  createTicketDraft: {} as CreateTicketDraft,
  updateTicketDraft: {} as UpdateTicketDraft,
  skipPreferencesCall: false,
};

const setPortalAppPreferences = (
  preferences: Preferences,
): PortalPreferenceAction => ({
  type: Actions.SET_PORTAL_APP_PREFERENCES,
  preferences,
});

const setSkipPreferencesCall = (): PortalPreferenceAction => ({
  type: Actions.SKIP_PREFERENCES_CALL,
});

const setPreference = (
  preferenceCollection: {
    [key: string]:
      | boolean
      | number
      | string
      | IndividualUpdateTicketDraft
      | UpdateTicketDraftCollection;
  },
  preferenceSpace: string,
): PortalPreferenceAction => ({
  type: Actions.SET_PREFERENCE,
  preferenceCollection,
  preferenceSpace,
});

const deletePreferences = (preferenceSpace: string) => ({
  type: Actions.DELETE_PREFERENCES,
  preferenceSpace,
});

const portalPreferencesReducer = (
  state: Preferences = initialState,
  action: PortalPreferenceAction,
): Preferences => {
  switch (action.type) {
    case Actions.SET_PORTAL_APP_PREFERENCES:
      const preferences = action.preferences || initialState;
      /**
       * Welcome value is a edge case of portal preferences.
       * Current existing preferences don't have the Welcome value.
       * But we need it to show the modal. So we will inject it. The next time
       * the user calls for their preferences it will exist.
       */
      const welcome = preferences.welcome || { firstTimeUser: true };
      return {
        ...state,
        ...preferences,
        welcome,
      };
    case Actions.SET_PREFERENCE:
      const { preferenceSpace = '', preferenceCollection = {} } = action;
      return {
        ...state,
        [preferenceSpace]: {
          ...state[preferenceSpace],
          ...preferenceCollection,
        },
      };
    case Actions.DELETE_PREFERENCES:
      const preferenceKey = action.preferenceSpace || '';
      const newState = { ...state };
      delete newState[preferenceKey];
      return {
        ...newState,
      };
    case Actions.SKIP_PREFERENCES_CALL:
      return {
        ...state,
        skipPreferencesCall: true,
      };
    default:
      return state;
  }
};

const isInitialPreference = (compareState: {}) =>
  JSON.stringify(initialState) === JSON.stringify(compareState);

const onGeneralError = (
  dispatch: Dispatch<PortalPreferenceAction>,
  error: PreferenceError,
  preferenceKey: string,
) => {
  const preferences =
    error.status === HttpStatusCode.BAD_REQUEST
      ? initialState[preferenceKey]
      : failureState[preferenceKey];

  dispatch(setPreference(preferences, preferenceKey));
  /**
   * portal preferences call will continue failing throughout the session if it fails once.
   * stop the api call to Portal Ticketing
   * if the call gives a 431.
   */
  const isRequestTooLarge = error.status === HttpStatusCode.REQUEST_TOO_LARGE;
  if (isRequestTooLarge) {
    dispatch(setSkipPreferencesCall());
  }
};

/** PUT single Preference to Portal Preferences */
const setPortalPreference = (
  preferenceKey: string,
  value: {
    [key: string]:
      | string
      | number
      | boolean
      | IndividualUpdateTicketDraft
      | UpdateTicketDraftCollection;
  },
) => async (dispatch: Dispatch<PortalPreferenceAction>, getState: Function) => {
  const onError = (err: PreferenceError) =>
    onGeneralError(dispatch, err, preferenceKey);

  const onSuccess = () => dispatch(setPreference(value, preferenceKey));

  const preferenceState = getState().portalPreferences[preferenceKey];

  const payload = {
    ...preferenceState,
    ...value,
  };

  const isCreateOrUpdateTicketDraft =
    [
      NAMESPACE_PREFERENCES.CREATE_TICKET_DRAFT,
      NAMESPACE_PREFERENCES.UPDATE_TICKET_DRAFT,
    ].indexOf(preferenceKey) >= 0;

  if (
    isCreateOrUpdateTicketDraft &&
    getState().portalPreferences.skipPreferencesCall
  ) {
    return Promise.resolve();
  }

  return ApiRequest.put(`${API_PORTAL_PREFERENCES}${preferenceKey}`, payload, {
    dispatchId: RequestId.SET_PORTAL_PREFERENCE,
    onSuccess,
    onError,
    retries: isCreateOrUpdateTicketDraft ? 0 : API_DEFAULT_RETRIES,
  });
};

/** GET Preference for certain key */
const getPortalPreference = (preferenceKey: string) => (
  dispatch: Dispatch<PortalPreferenceAction>,
) => {
  const onError = (err: PreferenceError) =>
    onGeneralError(dispatch, err, preferenceKey);

  const onSuccess = (response: PreferenceResponse) =>
    dispatch(setPreference(response.data[preferenceKey], preferenceKey));

  return ApiRequest.get(`${API_PORTAL_PREFERENCES}${preferenceKey}`, {
    onSuccess,
    onError,
  });
};

/** GET all Preferences from Portal */
const getPortalPreferences = () => (
  dispatch: Dispatch<PortalPreferenceAction>,
) => {
  const onError = (err: PreferenceError) => {
    const preferences =
      err.status === HttpStatusCode.BAD_REQUEST
        ? firstTimeUserState
        : failureState;
    dispatch(setPortalAppPreferences(preferences));
    /**
     * portal preferences call will continue failing throughout the session if it fails once.
     * stop the api call to Portal Ticketing
     * if the call gives a 431.
     */
    const isRequestTooLarge = err.status === HttpStatusCode.REQUEST_TOO_LARGE;
    if (isRequestTooLarge) {
      dispatch(setSkipPreferencesCall());
    }
  };

  const onSuccess = (response: PreferenceResponse) =>
    dispatch(setPortalAppPreferences(response.data));

  return ApiRequest.get(`${API_PORTAL_PREFERENCES}`, {
    dispatchId: RequestId.GET_PORTAL_PREFERENCES,
    onSuccess,
    onError,
  });
};

const deletePortalPreferences = (preferenceKey: string) => (
  dispatch: Dispatch<PortalPreferenceAction>,
  getState: Function,
) => {
  const onError = (err: PreferenceError) =>
    onGeneralError(dispatch, err, preferenceKey);

  const onSuccess = (response: PreferenceResponse) =>
    dispatch(deletePreferences(preferenceKey));

  const isCreateOrUpdateTicketDraft =
    [
      NAMESPACE_PREFERENCES.CREATE_TICKET_DRAFT,
      NAMESPACE_PREFERENCES.UPDATE_TICKET_DRAFT,
    ].indexOf(preferenceKey) >= 0;
  if (
    isCreateOrUpdateTicketDraft &&
    getState().portalPreferences.skipPreferencesCall
  ) {
    return Promise.resolve();
  }

  const options = {
    onError,
    onSuccess,
    ...(preferenceKey === NAMESPACE_PREFERENCES.CREATE_TICKET_DRAFT && {
      dispatchId: RequestId.GET_PORTAL_PREFERENCES,
    }),
  };

  return ApiRequest.delete(
    `${API_PORTAL_PREFERENCES}${preferenceKey}`,
    options,
  );
};

export {
  getPortalPreference,
  getPortalPreferences,
  isInitialPreference,
  portalPreferencesReducer,
  setPortalPreference,
  deletePortalPreferences,
};
